<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import BetaLogoVariantCard from '@/components/beta-dashboard/molecules/BetaLogoVariantCard.vue'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import { useSiteStore } from '@/stores/site'

type LogoType = 'logo' | 'logo_mobile' | 'logo_dark' | 'logo_dark_mobile' | 'favicon'

const mediaManagerStore = useMediaManagerStore()
const siteStore = useSiteStore()

const saving = ref(false)
const logoSaving = ref<LogoType | null>(null)

const identity = reactive({
    name: '',
    description: '',
})

const lightLogos = computed(() => [
    {
        type: 'logo' as const,
        title: 'Desktop',
        description: 'Fundo claro',
        image: siteStore.logo,
        fallback: identity.name.charAt(0) || 'S',
    },
    {
        type: 'logo_mobile' as const,
        title: 'Mobile',
        description: 'Fundo claro',
        image: siteStore.logoMobile,
        fallback: identity.name.charAt(0) || 'S',
    },
])

const darkLogos = computed(() => [
    {
        type: 'logo_dark' as const,
        title: 'Desktop',
        description: 'Fundo escuro',
        image: siteStore.logoDark,
        fallback: identity.name.charAt(0) || 'S',
    },
    {
        type: 'logo_dark_mobile' as const,
        title: 'Mobile',
        description: 'Fundo escuro',
        image: siteStore.logoDarkMobile,
        fallback: identity.name.charAt(0) || 'S',
    },
])

watch(
    [() => siteStore.name, () => siteStore.siteDescription],
    ([name, description]) => {
        identity.name = String(name || '')
        identity.description = String(description || '')
    },
    { immediate: true },
)

const saveIdentity = async () => {
    if (saving.value) return
    saving.value = true

    try {
        await siteStore.handleUpdateSeo({
            userEmail: siteStore.mainEmail,
            activeSegment: siteStore.segment?.segment,
            activeSubsegments: siteStore.segment?.subSegments ?? [],
            userArtisticName: identity.name,
            siteLanguage: siteStore.language,
            siteDescription: identity.description,
            h1: siteStore.seoText,
            document: siteStore.documentId,
            location: siteStore.location,
            cookiebar: siteStore.cookiebar,
            blockCopyImage: siteStore.blockCopyImage,
        })
    } finally {
        saving.value = false
    }
}

const chooseLogo = (type: LogoType) => {
    if (logoSaving.value) return

    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setAction(async (media: any) => {
        if (!media?.content) return

        logoSaving.value = type
        try {
            await siteStore.handleUpdateLogo(media.content, type)
        } finally {
            logoSaving.value = null
        }
    })
    mediaManagerStore.setIsOpenModal(true)
}
</script>

<template>
    <div class="beta-identity">
        <div class="beta-identity__heading">
            <div>
                <p>Identidade</p>
                <h2>Como sua marca aparece</h2>
            </div>
            <span>Alterações de imagem são salvas após a seleção.</span>
        </div>

        <section class="beta-identity__logos" aria-labelledby="beta-identity-logos-title">
            <div class="beta-identity__section-heading">
                <div>
                    <h3 id="beta-identity-logos-title">Variações da marca</h3>
                    <p>Use versões com contraste adequado para cada fundo e dispositivo.</p>
                </div>
                <span>PNG recomendado</span>
            </div>

            <div class="beta-identity__logo-groups">
                <div class="beta-identity__logo-group">
                    <div class="beta-identity__logo-group-heading">
                        <strong>Fundos claros</strong>
                        <small>Logo preferencialmente escuro</small>
                    </div>
                    <div class="beta-identity__logo-grid">
                        <BetaLogoVariantCard
                            v-for="item in lightLogos"
                            :key="item.type"
                            :title="item.title"
                            :description="item.description"
                            :image="item.image"
                            :fallback="item.fallback"
                            :loading="logoSaving === item.type"
                            @select="chooseLogo(item.type)"
                        />
                    </div>
                </div>

                <div class="beta-identity__logo-group">
                    <div class="beta-identity__logo-group-heading">
                        <strong>Fundos escuros</strong>
                        <small>Logo preferencialmente claro</small>
                    </div>
                    <div class="beta-identity__logo-grid">
                        <BetaLogoVariantCard
                            v-for="item in darkLogos"
                            :key="item.type"
                            :title="item.title"
                            :description="item.description"
                            :image="item.image"
                            :fallback="item.fallback"
                            tone="dark"
                            :loading="logoSaving === item.type"
                            @select="chooseLogo(item.type)"
                        />
                    </div>
                </div>
            </div>

            <div class="beta-identity__favicon">
                <BetaLogoVariantCard
                    title="Favicon"
                    description="Ícone do navegador · 64 × 64 px"
                    :image="siteStore.favicon"
                    fallback="ico"
                    tone="neutral"
                    :loading="logoSaving === 'favicon'"
                    @select="chooseLogo('favicon')"
                />
            </div>
        </section>

        <form class="beta-identity__form" @submit.prevent="saveIdentity">
            <div class="beta-identity__section-heading">
                <div>
                    <h3>Informações principais</h3>
                    <p>Esses dados identificam o site e também são reutilizados pelo contrato legado.</p>
                </div>
            </div>

            <label>
                <span>Nome do site</span>
                <input v-model="identity.name" type="text" maxlength="100">
            </label>
            <label>
                <span>Descrição curta</span>
                <textarea
                    v-model="identity.description"
                    rows="4"
                    maxlength="300"
                    placeholder="Explique em poucas palavras o que você faz."
                />
            </label>
            <div class="beta-identity__form-actions">
                <button type="submit" :disabled="saving">
                    {{ saving ? 'Salvando…' : 'Salvar identidade' }}
                </button>
            </div>
        </form>
    </div>
</template>

<style scoped>
.beta-identity {
    display: grid;
    gap: 24px;
}

.beta-identity__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 20px;
    border-bottom: 1px solid #edf1f6;
}

.beta-identity__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-identity__heading h2 {
    margin-top: 4px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-identity__heading > span {
    color: #8291a7;
    font-size: 11px;
    text-align: right;
}

.beta-identity__logos,
.beta-identity__form {
    border: 1px solid #e6ebf2;
    border-radius: 20px;
    background: #f9fbfd;
    padding: 18px;
}

.beta-identity__section-heading {
    display: flex;
    align-items: start;
    justify-content: space-between;
    gap: 16px;
}

.beta-identity__section-heading h3 {
    color: #233852;
    font-size: 14px;
    font-weight: 850;
}

.beta-identity__section-heading p {
    margin-top: 4px;
    color: #7e8da2;
    font-size: 12px;
}

.beta-identity__section-heading > span {
    border-radius: 999px;
    background: #edf2f7;
    padding: 5px 8px;
    color: #7b899b;
    font-size: 10px;
    font-weight: 850;
    text-transform: uppercase;
}

.beta-identity__logo-groups {
    margin-top: 18px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.beta-identity__logo-group {
    min-width: 0;
    border-radius: 17px;
    background: #fff;
    padding: 12px;
}

.beta-identity__logo-group-heading {
    padding: 2px 2px 10px;
}

.beta-identity__logo-group-heading strong,
.beta-identity__logo-group-heading small {
    display: block;
}

.beta-identity__logo-group-heading strong {
    color: #30455f;
    font-size: 14px;
    font-weight: 850;
}

.beta-identity__logo-group-heading small {
    margin-top: 2px;
    color: #8a98aa;
    font-size: 10px;
}

.beta-identity__logo-grid {
    display: grid;
    gap: 9px;
}

.beta-identity__favicon {
    margin-top: 12px;
    width: min(100%, 360px);
}

.beta-identity__form {
    display: grid;
    gap: 17px;
    background: #fff;
}

.beta-identity__form label {
    display: grid;
    gap: 7px;
}

.beta-identity__form label > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-identity__form input,
.beta-identity__form textarea {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 14px;
    background: #fff;
    padding: 12px 13px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-identity__form input:focus,
.beta-identity__form textarea:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-identity__form-actions {
    display: flex;
    justify-content: flex-end;
}

.beta-identity__form-actions button {
    min-height: 43px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 17px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-identity__form-actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 767px) {
    .beta-identity__heading {
        align-items: start;
        flex-direction: column;
    }

    .beta-identity__heading > span {
        text-align: left;
    }

    .beta-identity__logo-groups {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 479px) {
    .beta-identity__logos,
    .beta-identity__form {
        border-radius: 16px;
        padding: 13px;
    }
}
</style>
